import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { LeveyJenningsChart, WestgardRule } from '@his/shared';
import { WESTGARD_DESCRIPTIONS } from '@his/shared';
import { get } from '@/lib/api';
import { getPages, type Paged } from './pagedList';
import { describeError, useAuth } from '@/lib/auth';
import { cn, dateOnly, dateTime, humanize } from '@/lib/utils';
import { DataTable, FilterField, PageHeader, SearchFilterBar, StatTile } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Select } from '@/components/ui/primitives';

export interface ControlLot {
  id: string;
  level: string;
  lotNo: string;
  manufacturer: string | null;
  expiryDate: string;
  targetMean: number;
  targetSd: number;
  expired: boolean;
  test: { id: string; code: string; name: string; unit: string | null };
}

interface QcGateFailedRun {
  level: string;
  lotNo: string;
  instrument: string | null;
  runAt: string;
  violations: string[];
}

interface QcGate {
  windowHours: number;
  blocked: { testId: string; testName: string; message: string; failedRuns: QcGateFailedRun[] }[];
}

function describeRules(violations: string[]): string {
  const rejecting = violations.filter((rule) => rule !== '1_2s');
  const shown = rejecting.length > 0 ? rejecting : violations;
  return shown.map((rule) => WESTGARD_DESCRIPTIONS[rule as WestgardRule] ?? rule).join('; ');
}

/**
 * Laboratory quality control (SRS 8.1).
 *
 * The Levey-Jennings chart is the screen: a technologist reads control data as
 * a picture with 2 SD and 3 SD lines on it, and a table of numbers does not
 * show a drift. Points that broke a rejection rule are drawn in red and stay
 * on the chart — a failed run is not something a lab gets to hide from itself.
 */
export function QcPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [testId, setTestId] = useState('');
  const [lotId, setLotId] = useState(params.get('lotId') ?? '');

  const tests = useQuery({
    queryKey: ['lab-tests'],
    queryFn: async () => (await get<Paged<{ id: string; code: string; name: string; unit: string | null }>>('/lab/tests?limit=300')).data,
  });

  const lots = useQuery({
    queryKey: ['qc-lots', testId],
    queryFn: () => getPages<ControlLot>('/lab/qc/lots', { testId: testId || undefined, limit: 200 }),
  });

  const chart = useQuery({
    queryKey: ['qc-chart', lotId],
    queryFn: () => get<LeveyJenningsChart>(`/lab/qc/lots/${lotId}/chart?days=60`),
    enabled: Boolean(lotId),
  });

  const gate = useQuery({
    queryKey: ['qc-gate'],
    queryFn: () => get<QcGate>('/lab/qc/gate'),
  });

  const lot = lots.data?.find((l) => l.id === lotId);

  return (
    <>
      <PageHeader
        title={tr('lab', 'quality_control')}
        description={tr('lab', 'control_lots_westgard_rules_and_levey_jennings')}
        actions={
          <>
            {can('lab.master.manage') && (
              <Button variant="outline" onClick={() => navigate(testId ? `/lab/qc/lots/new?testId=${testId}` : '/lab/qc/lots/new')}>
                {tr('lab', 'add_lot')}
              </Button>
            )}
            {can('lab.result.enter') && (
              <Button onClick={() => navigate(`/lab/qc/runs/new?lotId=${lotId}${testId ? `&testId=${testId}` : ''}`)} disabled={!lotId}>
                {tr('lab', 'record_a_run')}
              </Button>
            )}
          </>
        }
      />

      <QcGateBanner gate={gate.data} error={gate.error} />

      <SearchFilterBar
        filters={
          <FilterField label={tr('lab', 'test')}>
            <Select value={testId} onChange={(e) => setTestId(e.target.value)} className="w-56">
              <option value="">{tr('lab', 'all_tests')}</option>
              {tests.data?.map((test) => (
                <option key={test.id} value={test.id}>
                  {test.name}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={testId ? () => setTestId('') : undefined}
      />

      <div className="mb-4">
        <DataTable
          rows={lots.data}
          loading={lots.isLoading} refreshing={lots.isFetching}
          error={lots.error}
          rowKey={(row) => row.id}
          onRowClick={(row) => setLotId(row.id)}
          empty={tr('lab', 'no_control_lots_configured')}
          columns={[
            { header: tr('lab', 'test'), sort: (row) => row.test.name, cell: (row) => row.test.name },
            { header: tr('lab', 'level'), sort: (row) => row.level, cell: (row) => <Badge variant="secondary">{humanize(row.level)}</Badge> },
            { header: tr('lab', 'lot'), sort: (row) => row.lotNo, cell: (row) => <span className="font-mono text-xs">{row.lotNo}</span> },
            { header: tr('lab', 'target_mean'), align: 'right', sort: (row) => row.targetMean, cell: (row) => `${row.targetMean} ${row.test.unit ?? ''}` },
            { header: tr('lab', 'sd'), align: 'right', sort: (row) => row.targetSd, cell: (row) => row.targetSd },
            {
              header: tr('lab', 'expires'),
              sort: (row) => row.expiryDate,
              cell: (row) => (
                <span className={cn(row.expired && 'font-semibold text-destructive')}>{dateOnly(row.expiryDate)}</span>
              ),
            },
            {
              header: '',
              align: 'right',
              cell: (row) => (row.id === lotId ? <Badge variant="default">{tr('lab', 'charted')}</Badge> : null),
            },
          ]}
        />
      </div>

      {chart.data && <LeveyJennings chart={chart.data} unit={lot?.test.unit ?? null} />}
    </>
  );
}

function QcGateBanner({ gate, error }: { gate: QcGate | undefined; error: unknown }) {
  if (error) {
    return (
      <Alert variant="warning" className="mb-4">
        {tr('lab', 'could_not_check_which_tests_are_closed_for')} {describeError(error)}
      </Alert>
    );
  }
  if (!gate || gate.blocked.length === 0) return null;

  return (
    <Alert
      variant="destructive"
      className="mb-4"
      title={`Result entry is closed for ${gate.blocked.length} test${gate.blocked.length === 1 ? '' : 's'}`}
    >
      <ul className="mt-2 space-y-3">
        {gate.blocked.map((entry) => (
          <li key={entry.testId}>
            <p className="font-medium">{entry.testName}</p>
            <p>{entry.message}</p>
            <ul className="mt-1 space-y-0.5 text-xs">
              {entry.failedRuns.map((run) => (
                <li key={`${run.level}-${run.lotNo}-${run.instrument ?? ''}-${run.runAt}`}>
                  {humanize(run.level)} {tr('lab', 'level_lot')} {run.lotNo}
                  {run.instrument ? ` · ${run.instrument}` : ''} · {dateTime(run.runAt)} · {describeRules(run.violations)}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs">
        {tr('lab', 'failed_runs_from_the_last')} {gate.windowHours} {tr('lab', 'hours_count_record_a_passing_control_run_for')}
      </p>
    </Alert>
  );
}

function LeveyJennings({ chart, unit }: { chart: LeveyJenningsChart; unit: string | null }) {
  const { targetMean: mean, targetSd: sd } = chart;
  const data = chart.points.map((point, index) => ({
    index: index + 1,
    value: point.value,
    runAt: point.runAt,
    accepted: point.accepted,
    violations: point.violations,
  }));

  const rejected = chart.points.filter((p) => !p.accepted);

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <div>
          <CardTitle className="text-sm">
            {chart.testName} · {humanize(chart.level)} {tr('lab', 'lot_2')} {chart.lotNo}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {tr('lab', 'target')} {chart.targetMean} ± {chart.targetSd} {unit ?? ''}
          </p>
        </div>
        <div className="flex gap-3">
          <StatTile label={tr('lab', 'runs')} value={chart.points.length} />
          <StatTile label={tr('lab', 'observed_mean')} value={chart.observedMean ?? '—'} />
          <StatTile
            label={tr('lab', 'cv')}
            value={chart.cvPct === null ? '—' : `${chart.cvPct}%`}
            tone={(chart.cvPct ?? 0) > 5 ? 'warning' : 'success'}
          />
          <StatTile label={tr('lab', 'rejected')} value={rejected.length} tone={rejected.length ? 'destructive' : 'success'} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="index" tick={{ fontSize: 11 }} />
              <YAxis
                domain={[mean - 4 * sd, mean + 4 * sd]}
                tick={{ fontSize: 11 }}
                tickFormatter={(v: number) => v.toFixed(2)}
              />
              <Tooltip
                formatter={(value: number) => [`${value} ${unit ?? ''}`, 'Value']}
                labelFormatter={(index: number) => dateTime(data[Number(index) - 1]?.runAt)}
              />
              {/* The lines a technologist actually reads the chart against. */}
              <ReferenceLine y={mean} stroke="currentColor" strokeWidth={1.5} label={{ value: 'mean', fontSize: 10 }} />
              {[1, 2, 3].map((n) => (
                <ReferenceLine
                  key={`p${n}`}
                  y={mean + n * sd}
                  strokeDasharray="4 4"
                  stroke={n === 3 ? 'hsl(var(--destructive))' : 'currentColor'}
                  label={{ value: `+${n}SD`, fontSize: 10 }}
                />
              ))}
              {[1, 2, 3].map((n) => (
                <ReferenceLine
                  key={`m${n}`}
                  y={mean - n * sd}
                  strokeDasharray="4 4"
                  stroke={n === 3 ? 'hsl(var(--destructive))' : 'currentColor'}
                  label={{ value: `−${n}SD`, fontSize: 10 }}
                />
              ))}
              <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
              <Scatter
                dataKey="value"
                shape={(props: { cx?: number; cy?: number; payload?: { accepted: boolean } }) =>
                  props.payload?.accepted === false ? (
                    <circle cx={props.cx} cy={props.cy} r={6} fill="hsl(var(--destructive))" />
                  ) : (
                    <g />
                  )
                }
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {rejected.length > 0 && (
          <div className="mt-3 space-y-1">
            {rejected.map((point) => (
              <Alert key={point.id} variant="destructive">
                <span className="font-semibold">{dateTime(point.runAt)}</span> — {point.value}{' '}
                ({point.zScore > 0 ? '+' : ''}
                {point.zScore} {tr('lab', 'sd_2')}{' '}
                {point.violations
                  .map((rule) => WESTGARD_DESCRIPTIONS[rule as WestgardRule] ?? rule)
                  .join('; ')}
              </Alert>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
